import { useContext, useEffect, useState } from "react";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { criarCacheDeExpediente } from "./cacheDeExpediente";

// O expediente de cada conexão, para a tela dizer se está aberto agora.
//
// A CONTA é do servidor (`estadoDoHorario`): aqui só se guarda a resposta do
// `GET /horarios`, que já devolve o estado por conexão. Refazer a conta no
// navegador daria uma segunda régua — e ela discordaria do robô no primeiro
// feriado.
//
// Uma busca para o app inteiro, não uma por conversa: a lista de atendimentos
// tem dezenas de linhas e o cabeçalho remonta a cada troca de conversa. As
// regras do guardado (empresa, geração, limpar x invalidar) moram em
// `cacheDeExpediente.js`, que é testável sem navegador.
const VALIDADE_MS = 60 * 1000;

// De quanto em quanto tempo se confere se o que está guardado venceu.
//
// A resposta MUDA sozinha com o relógio: às 18:00 "Aberto · fecha às 18:00"
// vira "Fora do expediente" sem ninguém mexer em nada. Conferindo só na
// montagem, quem deixa a tela aberta a tarde inteira fica com o selo de
// manhã — e ele passa a mentir justamente na hora que importa.
const RELOGIO_MS = 30 * 1000;

const cache = criarCacheDeExpediente(async () => {
  const { data } = await api.get("/horarios");

  const porConexao = {};

  (data?.conexoes || []).forEach(item => {
    porConexao[item.whatsappId] = item.estado;
  });

  return porConexao;
}, VALIDADE_MS);

// Logout: esquece o que está em memória e NÃO manda ninguém buscar de novo —
// um GET saindo depois de sair da conta traria o expediente de volta para a
// tela de login.
export const limparCacheDeExpediente = companyId => cache.limpar(companyId);

// Quem acabou de gravar horário chama isto: além de esquecer, quem está
// montado busca AGORA. Esperar o relógio deixaria a tela de quem salvou meio
// minuto atrás mostrando o horário anterior.
export const invalidarExpediente = companyId => cache.invalidar(companyId);

// Devolve { [whatsappId]: estado }, com o estado do backend:
// { aberto, motivo, fechaAs, proximaAbertura }.
const SEM_NADA = {};

const useEstadoDeExpediente = () => {
  const { user } = useContext(AuthContext);
  const companyId = user?.companyId || null;

  // O valor sai do guardado na hora de renderizar; o estado aqui é só um
  // contador que manda repintar. Guardar uma CÓPIA do mapa faria a tela
  // continuar mostrando o expediente da empresa anterior enquanto o aviso da
  // troca não chegasse.
  const [, setVersao] = useState(0);

  useEffect(() => {
    if (!companyId) return undefined;

    let vivo = true;

    const desassinar = cache.assinar((empresaDoAviso, recarregar) => {
      // Aviso de outra empresa não repinta esta tela.
      if (!vivo || (empresaDoAviso && empresaDoAviso !== companyId)) return;

      setVersao(v => v + 1);

      // Só a INVALIDAÇÃO pede busca nova. No logout o aviso chega com
      // `recarregar` falso, e é isso que impede o guardado de renascer com
      // dado de quem acabou de sair.
      if (recarregar && cache.venceu(companyId)) cache.buscar(companyId);
    });

    const conferir = () => {
      if (cache.venceu(companyId)) cache.buscar(companyId);
    };

    conferir();

    // O relógio é o que mantém o selo honesto: às 18:00 "fecha às 18:00" vira
    // "fora do expediente" sem ninguém mexer em nada, e conferir só na
    // montagem deixava a tela aberta mentindo a tarde inteira.
    const relogio = setInterval(conferir, RELOGIO_MS);

    return () => {
      vivo = false;
      desassinar();
      clearInterval(relogio);
    };
  }, [companyId]);

  if (!companyId) return SEM_NADA;

  return cache.valorDe(companyId) || SEM_NADA;
};

export default useEstadoDeExpediente;
